import { ChangeDetectorRef, Component, HostListener, Input, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { INavigationItem, INavigationSection } from '@app/main/interfaces';
import { NavigationService } from '@app/main/navigation.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { MenuDisplayMode } from './navigation-menu.interface';
import { SessionService } from '@app/shared/services/session/session.service';
import { AFTER_LOGIN_REDIRECT } from '@app/shared/constants/localstorage-keys';
import { MixpanelService } from '@app/shared/services/mixpanel/mixpanel.service';
import { mixpanelEventTypes } from '@app/shared/enums/mixpanel.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@Component({
  selector: 'cm-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss', '../menu-navigation-item/menu-navigation-item.component.scss'],
})
@UntilDestroy()
export class NavigationMenuComponent implements OnInit {
  @Input() navigationItems: INavigationItem[];
  @Input() displayMode: MenuDisplayMode;
  @Input() showFavoriteOption: boolean;

  public MenuDisplayMode = MenuDisplayMode;

  isMobile: boolean;
  sectionSubMenu: string;
  selectedSection: string;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private mixpanelService: MixpanelService,
    public navigationService: NavigationService,
    private router: Router,
    private sessionStorage: SessionService
  ) {
    this.isMobile = this.displayMode === MenuDisplayMode.PROFILE ? true : UtilService.isMobileAndTabletSize();
  }

  ngOnInit(): void {
    this.isMobile = this.displayMode === MenuDisplayMode.PROFILE ? true : UtilService.isMobileAndTabletSize();
    this.router.events.subscribe((event: any) => {
      // The /state is added by the msal authentication,
      // so this is after login, we need to redirect the user to the originlly requested url
      if (event instanceof NavigationStart && event.url === '/state') {
        const url: string = this.sessionStorage.getItem(AFTER_LOGIN_REDIRECT);

        // if the stored url contains query params, we need to send them in the { queryParams: ... }
        // on the router.navigate() method, otherwise it will throw a 404
        if (url?.includes('?')) {
          const route = url.split('?')[0];
          const queryString = url.split('?')[1];
          const queryParams = new URLSearchParams(queryString);
          const paramsAsObject = Object.fromEntries(queryParams.entries());
          this.router.navigate([route], { queryParams: paramsAsObject });
          return;
        }

        this.router.navigate([url || '/']);
      }
    });

    this.setCurrentPageSection();
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.isMobile = this.displayMode === MenuDisplayMode.PROFILE ? true : UtilService.isMobileAndTabletSize();

    this.changeDetector.detectChanges();
  }

  get isShrinked() {
    return this.navigationService.isShrinked;
  }

  onMenuExpanded(section: string): void {
    this.mixpanelService.trackEvent(mixpanelEventTypes.SIDEBAR_EXPAND, {
      category: section,
    });
  }

  onMenuCollapsed(section: string): void {
    this.mixpanelService.trackEvent(mixpanelEventTypes.SIDEBAR_COLLAPSE, {
      category: section,
    });
  }

  trackNavigateEvent(navigationItem: INavigationItem): void {
    this.mixpanelService.trackEvent(mixpanelEventTypes.SIDEBAR_NAVIGATE, {
      navigateTo: navigationItem.routerLink,
    });
  }

  toggleSubMenu(section: string): void {
    this.sectionSubMenu = section;
  }

  setCurrentPageSection(): void {
    this.navigationService.selectedSection$.pipe(untilDestroyed(this)).subscribe((selectedSection) => {
      this.selectedSection = selectedSection;
    });
  }
}
